import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowsClockwise, Funnel, MagnifyingGlass, ClockClockwise, ShieldCheck, X, IdentificationCard,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable } from '@/components/ui/DataTable';
import { toast } from 'sonner';
import { formatDateTimeFull } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Action prefix → visual category
const CATEGORY = {
  'auth':            { label: '認証',         tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  'user':            { label: 'ユーザー',     tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  'employee':       { label: '社員',         tone: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  'department':     { label: '部署',         tone: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' },
  'submission':     { label: '申請',         tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  'email_settings': { label: 'メール設定',   tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  'email_template': { label: 'テンプレート', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  'email_log':      { label: 'メールログ',   tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'scheduled_emails': { label: 'スケジュール', tone: 'bg-teal-50 text-teal-700 border-teal-200' },
};

function categoryOf(action) {
  if (!action) return CATEGORY.user;
  const head = action.split('.')[0];
  return CATEGORY[head] || { label: head, tone: 'bg-zinc-50 text-zinc-700 border-zinc-200' };
}

function fmtDate(iso) {
  if (!iso) return '—';
  return formatDateTimeFull(iso) || iso;
}

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [logRes, actRes] = await Promise.all([
        axios.get(`${API}/audit-log`, { params: { limit: 500 } }),
        axios.get(`${API}/audit-log/actions`),
      ]);
      setItems(logRes.data || []);
      setActions(actRes.data || []);
    } catch (_e) {
      toast.error('監査ログの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((row) => {
      if (categoryFilter !== 'all' && !(row.action || '').startsWith(categoryFilter + '.')) return false;
      if (actionFilter !== 'all' && row.action !== actionFilter) return false;
      if (!q) return true;
      const hay = [
        row.actor_email, row.actor_name, row.action, row.action_label,
        row.target_label, row.target_id, row.ip_address,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, actionFilter, categoryFilter]);

  const visibleActions = useMemo(() => {
    if (categoryFilter === 'all') return actions;
    return actions.filter((a) => a.action.startsWith(categoryFilter + '.'));
  }, [actions, categoryFilter]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">設定</p>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
            <ShieldCheck size={28} weight="duotone" className="text-teal-700" />
            監査ログ
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            管理者操作の記録（誰が・いつ・何を）。古い順に最大 500 件まで表示します。
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="audit-reload">
          <ArrowsClockwise size={16} className="mr-1" /> 再読み込み
        </Button>
      </header>

      {/* Filters */}
      <section className="bg-white rounded-xl border border-zinc-200 p-4 flex flex-wrap items-center gap-3">
        <Funnel size={16} weight="duotone" className="text-zinc-400" />
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="メール・氏名・対象で検索"
            className="pl-9 h-9 text-sm"
            data-testid="audit-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700">
              <X size={12} />
            </button>
          )}
        </div>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setActionFilter('all'); }}>
          <SelectTrigger className="w-[160px] h-9" data-testid="audit-filter-category"><SelectValue placeholder="カテゴリ" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全カテゴリ</SelectItem>
            {Object.entries(CATEGORY).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[200px] h-9" data-testid="audit-filter-action"><SelectValue placeholder="アクション" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全アクション</SelectItem>
            {visibleActions.map((a) => (
              <SelectItem key={a.action} value={a.action}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-zinc-500 tabular-nums">
          {filtered.length} / {items.length} 件
        </div>
      </section>

      {/* Table */}
      {loading ? (
        <section className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <p className="px-5 py-12 text-center text-sm text-zinc-500">読み込み中…</p>
        </section>
      ) : (
        <DataTable
          dataTestId="audit-table"
          rows={filtered}
          rowKey={(row) => row.id}
          rowTestId={(row) => `audit-row-${row.id}`}
          empty={{ icon: <ShieldCheck size={36} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: items.length === 0 ? 'まだ操作ログがありません。' : '条件に一致するログはありません。' }}
          columns={[
            { key: 'created_at', header: '日時', cell: (row) => (
              <span className="inline-flex items-center gap-1.5 text-zinc-700 tabular-nums whitespace-nowrap">
                <ClockClockwise size={12} className="text-zinc-300" />{fmtDate(row.created_at)}
              </span>
            ) },
            { key: 'category', header: '', cell: (row) => {
              const cat = categoryOf(row.action);
              return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cat.tone}`}>{cat.label}</span>;
            } },
            { key: 'action', primary: true, header: 'アクション', cell: (row) => (
              <span className="text-zinc-900 font-medium">{row.action_label || row.action}<span className="ml-1 text-[10px] text-zinc-400 font-mono">{row.action}</span></span>
            ) },
            { key: 'actor', header: '実行者', cell: (row) => (
              row.actor_name || row.actor_email ? (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-bold shadow-sm shrink-0">
                    {(row.actor_name || row.actor_email || '?').slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="text-zinc-900 truncate">{row.actor_name || '—'}</div>
                    <div className="text-[10px] text-zinc-400 truncate">{row.actor_email}</div>
                  </div>
                </div>
              ) : <span className="text-zinc-400 text-xs">システム</span>
            ) },
            { key: 'target', header: '対象', cell: (row) => (
              <div className="max-w-[260px]">
                {row.target_label ? <div className="text-zinc-800 truncate" title={row.target_label}>{row.target_label}</div> : <span className="text-zinc-400">—</span>}
                {row.target_id && <div className="text-[10px] text-zinc-400 font-mono truncate" title={row.target_id}><IdentificationCard size={10} className="inline mr-0.5" />{row.target_id}</div>}
                {/* metadata は消えない記録（audit_log は挿入専用）。由来の解除など
                    「何が消されたか・なぜ」がここにしか残らない操作があるので必ず出す。 */}
                {row.metadata && Object.keys(row.metadata).length > 0 && (
                  <div className="mt-1 space-y-0.5 rounded bg-zinc-50 p-1.5" data-testid={`audit-metadata-${row.id}`}>
                    {Object.entries(row.metadata).filter(([, v]) => v !== null && v !== '').map(([k, v]) => (
                      <div key={k} className="text-[10px] leading-snug text-zinc-600">
                        <span className="font-mono text-zinc-400">{k}</span>
                        <span className="ml-1 break-all text-zinc-700">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) },
            { key: 'ip', header: 'IP', cell: (row) => <span className="text-zinc-500 text-xs font-mono">{row.ip_address || '—'}</span> },
          ]}
        />
      )}
    </div>
  );
}
